import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Users, Megaphone, CalendarDays, CheckSquare, BarChart3 } from "lucide-react";
import { AccountsPanel } from "./social/AccountsPanel";
import { CampaignsPanel } from "./social/CampaignsPanel";
import { CalendarQueuePanel } from "./social/CalendarQueuePanel";
import { ApprovalQueue } from "./social/ApprovalQueue";
import { ContentHealthOverview } from "./social/ContentHealthOverview";

type SocialTab = "overview" | "approval" | "accounts" | "campaigns" | "calendar";

const sans  = "'Instrument Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const T = {
  navy:   "#002868",
  red:    "#B22234",
  gold:   "#C5A55A",
  border: "rgba(27,27,27,0.12)",
};

export function SocialMediaManager() {
  const [activeTab, setActiveTab] = useState<SocialTab>("overview");

  /* Live pending count for the badge on the Approval tab */
  const { data: pendingCount = 0 } = useQuery({
    queryKey: ["social_pending_count"],
    queryFn: async () => {
      const { count } = await supabase
        .from("social_posts" as any)
        .select("id", { count: "exact", head: true })
        .eq("status", "pending_approval");
      return count ?? 0;
    },
    refetchInterval: 30_000,
  });

  const TABS: {
    id: SocialTab;
    label: string;
    icon: React.ReactNode;
    badge?: number;
  }[] = [
    { id: "overview",  label: "Overview",          icon: <BarChart3   size={14} /> },
    {
      id: "approval",
      label: "Approval Queue",
      icon: <CheckSquare size={14} />,
      badge: pendingCount > 0 ? pendingCount : undefined,
    },
    { id: "accounts",  label: "Accounts",          icon: <Users      size={14} /> },
    { id: "campaigns", label: "Campaigns",          icon: <Megaphone  size={14} /> },
    { id: "calendar",  label: "Calendar & Queue",   icon: <CalendarDays size={14} /> },
  ];

  return (
    <div>
      {/* Inner tab bar */}
      <div style={{
        display: "flex",
        gap: 0,
        borderBottom: `1px solid ${T.border}`,
        marginBottom: 24,
        backgroundColor: "rgba(255,255,255,0.7)",
        borderRadius: "8px 8px 0 0",
        padding: "0 4px",
        border: `1px solid ${T.border}`,
        borderBottomColor: T.border,
      }}>
        {TABS.map(tab => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "11px 18px",
                border: "none",
                borderBottom: active ? `2px solid ${T.red}` : "2px solid transparent",
                backgroundColor: active ? "rgba(178,34,52,0.05)" : "transparent",
                color: active ? T.navy : "rgba(27,27,27,0.62)",
                fontSize: 12,
                fontFamily: sans,
                fontWeight: active ? 700 : 500,
                cursor: "pointer",
                transition: "all 0.15s",
                whiteSpace: "nowrap",
                marginBottom: -1,
                position: "relative",
              }}
              onMouseEnter={e => {
                if (!active) {
                  (e.currentTarget as HTMLButtonElement).style.color = T.navy;
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(0,40,104,0.04)";
                }
              }}
              onMouseLeave={e => {
                if (!active) {
                  (e.currentTarget as HTMLButtonElement).style.color = "rgba(27,27,27,0.62)";
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent";
                }
              }}
            >
              <span style={{ opacity: active ? 1 : 0.7 }}>{tab.icon}</span>
              {tab.label}
              {tab.badge !== undefined && (
                <span style={{
                  backgroundColor: T.red,
                  color: "#fff",
                  borderRadius: 10,
                  padding: "1px 7px",
                  fontSize: 10,
                  fontWeight: 700,
                  lineHeight: "16px",
                  minWidth: 18,
                  textAlign: "center",
                  fontFamily: sans,
                }}>
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab panels */}
      <div style={{ display: activeTab === "overview"   ? "block" : "none" }}><ContentHealthOverview /></div>
      <div style={{ display: activeTab === "approval"   ? "block" : "none" }}><ApprovalQueue /></div>
      <div style={{ display: activeTab === "accounts"   ? "block" : "none" }}><AccountsPanel /></div>
      <div style={{ display: activeTab === "campaigns"  ? "block" : "none" }}><CampaignsPanel /></div>
      <div style={{ display: activeTab === "calendar"   ? "block" : "none" }}><CalendarQueuePanel /></div>
    </div>
  );
}
